import { FC, useEffect, useRef, useState } from 'react';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	getServicesRequest,
	updateMethodServicesRequest,
} from 'redux/reducers/paymentMethods/reducer';
import {
	getPaymentMethodsBlockedMessage,
	getPaymentServices,
	getUpdateMethodServicesError,
	getUpdateMethodServicesLoading,
} from 'redux/reducers/paymentMethods/selectors';
import { TPaymentMethod, TServicesSelection } from 'services/api/paymentMethods/types';
import { isServicesSelectionValid, ServicesSelector } from '../ServicesSelector';
import { ClientAccessBanner } from '../ClientAccess';

type TPaymentMethodServicesDialogProps = {
	method: TPaymentMethod | null;
	onClose: () => void;
};

export const PaymentMethodServicesDialog: FC<TPaymentMethodServicesDialogProps> = ({
	method,
	onClose,
}) => {
	const dispatch = useDispatch();
	const services = useSelector(getPaymentServices);
	const saving = useSelector(getUpdateMethodServicesLoading);
	const saveError = useSelector(getUpdateMethodServicesError);
	// blocked by the operator while the dialog is open (e.g. after a 423 of the save)
	const blockedMessage = useSelector(getPaymentMethodsBlockedMessage);
	const [selection, setSelection] = useState<TServicesSelection>({
		allServices: true,
		serviceIds: [],
	});
	const prevSaving = useRef(false);

	useEffect(() => {
		if (!method) return;
		setSelection({
			allServices: method.allServices,
			serviceIds: method.services.map((service) => service.id),
		});
		if (!services) dispatch(getServicesRequest());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [method]);

	// close after a successful save
	useEffect(() => {
		if (prevSaving.current && !saving && !saveError && method) onClose();
		prevSaving.current = saving;
	}, [saving, saveError, method, onClose]);

	const handleSave = () => {
		if (!method || blockedMessage) return;
		dispatch(
			updateMethodServicesRequest({
				id: method.id,
				allServices: selection.allServices,
				serviceIds: selection.allServices ? [] : selection.serviceIds,
			}),
		);
	};

	return (
		<Dialog
			open={!!method}
			onClose={saving ? undefined : onClose}
			maxWidth="xs"
			fullWidth
			sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
		>
			<DialogTitle color="primary">Edit services</DialogTitle>
			<DialogContent>
				<ClientAccessBanner messages={[blockedMessage]} sx={{ mb: 1 }} />
				<Typography sx={{ mb: 1 }}>{method?.label}</Typography>
				<Typography sx={{ fontSize: '.875rem', color: 'text.secondary', mb: 1 }}>
					Select the services this payment method pays for. The change does not start a payment.
				</Typography>
				<ServicesSelector
					services={services}
					value={selection}
					onChange={setSelection}
					disabled={saving || !!blockedMessage}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={saving}>
					Cancel
				</Button>
				<Button
					variant="contained"
					onClick={handleSave}
					disabled={saving || !isServicesSelectionValid(selection) || !!blockedMessage}
				>
					{saving ? <CircularProgress size={20} /> : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
